// front/src/index.js
import React, { useMemo, useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { BrowserRouter } from "react-router-dom";

// bootstrap
import "bootstrap/dist/css/bootstrap.min.css";
// react toastify
import "react-toastify/dist/ReactToastify.css";
import "./assets/scss/style.scss";

// context api
import { GlobalProvider, NavbarProvider, AdvancedProvider } from "./context";

// redux store
import { Provider } from "react-redux";
import { store } from "./app/store";

// react-query
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import MatchedAssetsProvider from "./context/matchedAssetsContext";
import MobileContextProvider from "./context/MobileContext";
import { queryClientConfig } from "./configs/query.config";

// i18n
import { I18nProvider, useI18n } from "./i18n/I18nProvider";

// 🌍 MUI
import { ThemeProvider, CssBaseline } from "@mui/material";
import { CacheProvider } from "@emotion/react";
import createEmotionCache from "./utils/createEmotionCache";
import rtlTheme from "./theme/rtlTheme";
import ltrTheme from "./theme/ltrTheme";

const queryClient = new QueryClient(queryClientConfig);
const root = ReactDOM.createRoot(document.getElementById("root"));

function MuiBridge({ children }) {
  const { dir, isRtl } = useI18n();

  // ✅ cache MUST change when dir changes
  const cache = useMemo(() => createEmotionCache(dir), [dir]);

  // ✅ theme in sync
  const theme = useMemo(() => (isRtl ? rtlTheme : ltrTheme), [isRtl]);

  /**
   * ✅ CRITICAL FIX:
   * MUI Dialog/Menu/Popover render with Portal into document.body.
   * If body/html has no dir, computed direction becomes LTR even if wrapper div is RTL.
   */
  useEffect(() => {
    try {
      document.documentElement.setAttribute("dir", dir);
      document.body.setAttribute("dir", dir);

      document.body.classList.toggle("rtl", dir === "rtl");
      document.body.classList.toggle("ltr", dir === "ltr");

      document.documentElement.style.direction = dir;
      document.body.style.direction = dir;
    } catch (e) {
      // noop
    }
  }, [dir]);

  return (
    // ✅ IMPORTANT: force remount when dir changes
    <CacheProvider value={cache} key={dir}>
      <ThemeProvider theme={theme} key={dir}>
        <CssBaseline />

        {/* wrapper still fine, but portals need html/body dir */}
        <div id="__mui_bridge_root" dir={dir} data-dir={dir} data-isrtl={isRtl ? "1" : "0"}>
          {children}
        </div>
      </ThemeProvider>
    </CacheProvider>
  );
}

root.render(
  <I18nProvider>
    <MuiBridge>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <GlobalProvider>
            <NavbarProvider>
              <AdvancedProvider>
                <MobileContextProvider>
                  <Provider store={store}>
                    <MatchedAssetsProvider>
                      <App />
                    </MatchedAssetsProvider>
                  </Provider>
                </MobileContextProvider>
              </AdvancedProvider>
            </NavbarProvider>
          </GlobalProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </MuiBridge>
  </I18nProvider>
);
